import React, { useEffect, useState, useCallback } from "react";
import { fetchKeyMetrics } from "../../api";
import { useUser } from "../../context/UserContext";
import KeyMetrics from "../../components/layout/KeyMetrics";
import StationOverview from "./StationOverview";
import Filter from "../../components/layout/Filter";
import "./dashboardOverview.scss";
import { Clock, Zap, User } from "lucide-react";

function DashboardOverview() {
  /* ------------------------------------------
     1️⃣  Pull the user and the loading flag
     ------------------------------------------ */
  const { user, isLoading: userLoading } = useUser();

  /* ------------------------------------------
     2️⃣  Component state
     ------------------------------------------ */
  const [metrics, setMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true); // API fetch flag
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
  });

  /* ------------------------------------------
     3️⃣  Utility – format dates as ISO strings
     ------------------------------------------ */
  const formatLocalDate = (date: Date, endOfDay = false) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const time = endOfDay ? "23:59:59" : "00:00:00";
    return `${year}-${month}-${day}T${time}`;
  };

  const formatINR = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  /* ------------------------------------------
     4️⃣  Fetch metrics – memoised with useCallback
     ------------------------------------------ */
  const loadMetrics = useCallback(
    async (startDate: Date, endDate: Date) => {
      /* If the user or org_id is still missing, bail out early. */
      if (!user?.org_id) {
        console.warn("Dashboard: org_id missing – skipping fetch");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const start = formatLocalDate(startDate, false);
        const end = formatLocalDate(endDate, true);

        const data = await fetchKeyMetrics(user.org_id, start, end);

        if (!data || data.status === 404) {
          throw new Error("No data found for the selected date range.");
        }

        /* Build the metric objects that the UI component expects */
        const formatted = [
          {
            label: "Total Revenue",
            value: formatINR(data.total_revenue),
            change: `${data.revenue_change_percent}%`,
            since: "vs last period",
            isPositive: data.revenue_change_percent > 0,
            icon: Zap,
          },
          {
            label: "Active Sessions",
            value: data.total_sessions.toString(),
            change: `${data.sessions_change_percent}%`,
            since: "vs last period",
            isPositive: data.sessions_change_percent > 0,
            icon: Clock,
          },
          {
            label: "Energy Consumed",
            value: `${data.total_energy_consumed.toFixed(2)} kWh`,
            change: `${data.energy_change_percent}%`,
            since: "vs last period",
            isPositive: data.energy_change_percent > 0,
            icon: Zap,
          },
          {
            label: "Active Chargers",
            value: data.active_chargers.toString(),
            since: "",
            isPositive: true,
            icon: User,
          },
        ];
        setMetrics(formatted);
      } catch (err: any) {
        console.error("Dashboard: Failed to load key metrics", err);
        /* Fallback to a human‑readable message */
        setError(err.detail || err.message || "Failed to load data.");
      } finally {
        setLoading(false);
      }
    },
    [user] // Re‑create only when the user changes
  );

  /* ------------------------------------------
     5️⃣  Run the fetch whenever we have a valid org_id
     ------------------------------------------ */
  useEffect(() => {
    /* We wait until the user context has finished loading *and* a valid
       org_id is present. */
    if (
      !userLoading &&
      user?.org_id &&
      dateRange.startDate &&
      dateRange.endDate
    ) {
      loadMetrics(dateRange.startDate, dateRange.endDate);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [userLoading, user?.org_id, dateRange]);

  /* ------------------------------------------
     6️⃣  UI rendering
     ------------------------------------------ */
  /* While the user context is still loading, show the global loader. */
  if (userLoading) {
    return (
      <div className="loader-container">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="dashboard-layout-wrapper">
      <div className="dashboard-header">
        <h2 className="dashboard-title">Key Metrics</h2>

        <Filter
          defaultStart={dateRange.startDate}
          defaultEnd={dateRange.endDate}
          onApply={(start, end) =>
            setDateRange({ startDate: start, endDate: end })
          }
        />
      </div>

      {/* API loading / error / success states */}
      {loading ? (
        <div className="loader-container">
          <div className="loader" />
        </div>
      ) : error ? (
        <div className="error-box">
          <p>⚠️ {error}</p>
        </div>
      ) : (
        <KeyMetrics metrics={metrics} />
      )}

      <div className="dashboard-grid-row">
        <div className="dashboard-main-content">
          <StationOverview />
        </div>
        {/* <div className="dashboard-sidebar-column"><AlertsSection /></div> */}
      </div>
      {/* <RecentActivity /> */}
      {/* <QuickActions /> */}
    </div>
  );
}

export default DashboardOverview;
